import {
	Component,
	EventEmitter,
	inject,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import {
	FormBuilder,
	FormControl,
	UntypedFormControl,
	Validators,
} from '@angular/forms';
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { CommonDialog } from '@lmc-app/shared/components/dialog/common-dialog';
import { CreateFlinksRequestRequest } from '@lmc-app/shared/interface/request/create-flinks-request.request';
import { ReplaySubject, Subject, takeUntil } from 'rxjs';
import { ApplicationService } from '@lmc-app/core/services/application.service';
import { User } from '@lmc-app/shared/interface/user.interface';
import { LegacyApiService } from '@lmc-app/core/services/legacy-api.service';
import { InveriteRequestRequest } from '@lmc-app/shared/interface/request/inverite-request.request';
import { MatSelectSearchUtil } from '@lmc-app/utils/mat-select-search.util';

@Component({
	selector: 'app-banking',
	templateUrl: './banking.component.html',
	styleUrls: ['./banking.component.scss'],
})
export class BankingComponent implements OnChanges, OnInit, OnDestroy {
	@Input() application: any;
	@Output() applicationChange = new EventEmitter<any>();
	@Input() currentUser: User;
	@Output() currentUserChange = new EventEmitter<any>();
	bankingForm = this.formBuilder.group({
		bankingMethod: [null, Validators.required],
		applicant: ['MAIN_APPLICANT', Validators.required],
	});
	inveriteForm = this.formBuilder.group({
		bank: [null, Validators.required],
	});
	inveriteBanks: InveriteBank[] = [];
	inveriteRequests: InveriteRequest[] = [];
	flinksRequests: FlinksRequest[] = [];
	isRefreshing = false;

	// Bank Statement properties
	accounts = [];
	account_labels = [];
	selected = new UntypedFormControl(0);
	bankStatement: any;
	homeFlag = true;
	showBankStatement = false;
	selectedInveriteRequest: any = null;

	displayedColumnsInveriteRequest: string[] = [
		'bank',
		'applicant',
		'status',
		'action',
	];
	dataSourceInveriteRequest = new MatTableDataSource(this.inveriteRequests);
	displayedColumnsFlinksRequest: string[] = ['applicant', 'status', 'view'];
	dataSourceFlinksRequest = new MatTableDataSource(this.flinksRequests);
	inveriteBanksFilter: FormControl<string> = new FormControl<string>('', {
		nonNullable: true,
	});
	public filteredInveriteBanks: ReplaySubject<InveriteBank[]> =
		new ReplaySubject<InveriteBank[]>(1);
	protected _onDestroy = new Subject<void>();
	private _snackBar = inject(MatSnackBar);
	private _dialog = inject(MatDialog);

	constructor(
		private apiService: LegacyApiService,
		private applicationService: ApplicationService,
		private formBuilder: FormBuilder
	) {}

	async ngOnInit() {
		this.getInveriteBanks();

		this.inveriteBanksFilter.valueChanges
			.pipe(takeUntil(this._onDestroy))
			.subscribe((searchValue) => {
				MatSelectSearchUtil.filterAllStringFields(
					this.inveriteBanks,
					searchValue,
					this.filteredInveriteBanks
				);
			});
	}

	ngOnChanges(changes: SimpleChanges) {
		this.application = changes.application?.currentValue;
		this.currentUser = changes.currentUser?.currentValue;
		if (this.application) {
			this.bankingForm.controls.bankingMethod.setValue(
				this.application.Banking_method
			);
			this.refreshRequests(this.application.id);
		}
	}

	getInveriteBanks() {
		this.apiService.getInveriteBanks().subscribe((res) => {
			this.inveriteBanks = res['banks'].sort((a, b) =>
				a.name.localeCompare(b.name)
			);
			this.filteredInveriteBanks.next(this.inveriteBanks.slice());
		});
	}

	createInveriteRequest() {
		let request: InveriteRequestRequest;
		if (this.bankingForm.controls.applicant.value === 'MAIN_APPLICANT') {
			request = {
				...this.bankingForm.getRawValue(),
				...this.inveriteForm.getRawValue(),
				name: this.application.Deal_Name,
				email: this.application.Email1,
				phone: this.application.Mobile,
			};
		} else {
			request = {
				...this.bankingForm.getRawValue(),
				...this.inveriteForm.getRawValue(),
				name: this.application.Co_Applicant_Name,
				email: this.application.Email,
				phone: this.application.Secondary_Cell_Number,
			};
		}
		this.applicationService
			.createInveriteRequest(this.application.id, request)
			.subscribe({
				next: (res) => {
					if (
						this.inveriteRequests.some(
							(inveriteRequest: InveriteRequest) => inveriteRequest.reused
						)
					) {
						this._snackBar.open(
							'Duplicated Inverite request. Request has been resent!',
							SnackbarConstants.ACTION_DISMISS,
							SnackbarConstants.DURATION_DISMISS_SHORT
						);
					}
					this.inveriteRequests = res;
					this.dataSourceInveriteRequest.data = this.inveriteRequests;
					this.inveriteForm.reset();
				},
				error: (res) => {
					console.error(res.error.message);
					this._snackBar.open(
						res.error.message,
						SnackbarConstants.ACTION_DISMISS,
						SnackbarConstants.DURATION_DISMISS_SHORT
					);
				},
			});
	}

	deleteInveriteRequest(inveriteId: string) {
		this._dialog
			.open(CommonDialog, {
				data: {
					title: 'Are you sure?',
					content: 'Confirm delete this Inverite request?',
				},
			})
			.afterClosed()
			.subscribe((result) => {
				if (result) {
					this.inveriteRequests = this.inveriteRequests.filter(
						(request) => request.guid !== inveriteId
					);

					this.applicationService
						.deleteInveriteRequest(this.application.id, inveriteId)
						.subscribe({
							next: (res) => {
								this.inveriteRequests = res;
								this.dataSourceInveriteRequest.data = this.inveriteRequests;
							},
							error: (error) => {
								console.error(error);
								this._snackBar.open(
									error,
									SnackbarConstants.ACTION_DISMISS,
									SnackbarConstants.DURATION_DISMISS_SHORT
								);
							},
						});
				}
			});
	}

	createFlinksRequest() {
		this._dialog
			.open(CommonDialog, {
				data: {
					title: 'Are you sure?',
					content: 'Confirm create Flinks request?',
				},
			})
			.afterClosed()
			.subscribe((result) => {
				if (result) {
					const request: CreateFlinksRequestRequest = {
						applicationId: this.application.id,
						name: this.application.Contact_Name.Full_Name,
						email: this.application.Email1,
						applicant: this.bankingForm.controls.applicant.value,
					};

					this.applicationService.createFlinksRequest(request).subscribe({
						next: (res) => {
							this.flinksRequests.push({
								uuid: res.uuid,
								applicationId: res.applicationId,
								applicant: res.applicant,
								status: res.status,
							} as FlinksRequest);
							this.dataSourceFlinksRequest.data = this.flinksRequests;
						},
						error: (error) => {
							if (error.status === 409) {
								this.refreshRequests(this.application.id);
								this._snackBar.open(
									'Duplicated Flinks request. Request has been resent!',
									SnackbarConstants.ACTION_DISMISS,
									SnackbarConstants.DURATION_DISMISS_SHORT
								);
							}
						},
					});
				}
			});
	}

	refreshRequests(applicationId: string) {
		this.isRefreshing = true;
		let inveriteComplete = false;
		let flinksComplete = false;

		const checkComplete = () => {
			if (inveriteComplete && flinksComplete) {
				this.isRefreshing = false;
			}
		};

		this.applicationService.getInveriteRequests(applicationId).subscribe({
			next: (res) => {
				this.inveriteRequests = res;
				this.dataSourceInveriteRequest.data = res;
			},
			error: (error) => {
				console.error(error);
				this._snackBar.open(
					error,
					SnackbarConstants.ACTION_DISMISS,
					SnackbarConstants.DURATION_DISMISS_SHORT
				);
				inveriteComplete = true;
				checkComplete();
			},
			complete: () => {
				inveriteComplete = true;
				checkComplete();
			},
		});

		this.applicationService
			.refreshFlinksRequestStatus(applicationId)
			.subscribe({
				next: async (res: any) => {
					this.flinksRequests = res;
					this.dataSourceFlinksRequest.data = this.flinksRequests;
				},
				error: (error) => {
					console.error(error);
					flinksComplete = true;
					checkComplete();
				},
				complete: () => {
					flinksComplete = true;
					checkComplete();
				},
			});
	}

	viewBankStatement(inveriteRequest: any) {
		if (inveriteRequest.status === 'Verified') {
			this.selectedInveriteRequest = inveriteRequest;
			this.showBankStatement = true;
			this.loadBankStatement();
		}
	}

	closeBankStatement() {
		this.showBankStatement = false;
		this.selectedInveriteRequest = null;
		this.clearBankStatement();
	}

	loadBankStatement() {
		if (this.selectedInveriteRequest) {
			this.showInveriteBankStatements(this.selectedInveriteRequest.inveriteId);
		}
	}

	hasProperties(something) {
		try {
			return Object.entries(something).length > 0;
		} catch (err) {
			return false;
		}
	}

	/**
	 * Format currency values with $ sign
	 */
	formatCurrency(value: string | number): string {
		if (value === null || value === undefined || value === '') {
			return '$0.00';
		}
		const numValue = typeof value === 'string' ? parseFloat(value) : value;
		if (isNaN(numValue)) {
			return '$0.00';
		}
		return new Intl.NumberFormat('en-CA', {
			style: 'currency',
			currency: 'CAD',
		}).format(numValue);
	}

	/**
	 * Parse float safely
	 */
	parseFloat(value: string | number): number {
		if (value === null || value === undefined || value === '') {
			return 0;
		}
		const numValue = typeof value === 'string' ? parseFloat(value) : value;
		return isNaN(numValue) ? 0 : numValue;
	}

	/**
	 * Format number with 2 decimal places
	 */
	formatNumber(value: string | number): string {
		if (value === null || value === undefined || value === '') {
			return '0.00';
		}
		const numValue = typeof value === 'string' ? parseFloat(value) : value;
		if (isNaN(numValue)) {
			return '0.00';
		}
		return numValue.toFixed(2);
	}

	monthlyTransactionSummary(index) {
		if (
			this.hasProperties(
				this.bankStatement.accounts[index]['statistics'][
					'monthly_category_stats'
				]
			)
		) {
			let monthly_category_stats =
				this.bankStatement.accounts[index]['statistics'][
					'monthly_category_stats'
				];
			return monthly_category_stats.length > 6
				? monthly_category_stats?.slice(0, 6)
				: monthly_category_stats;
		}
	}

	addTab(selectAfterAdding: boolean) {
		this.account_labels = [];
		this.accounts = [];
		for (var i = 0; i < this.bankStatement['accounts'].length; i++) {
			this.accounts.push(this.bankStatement['accounts'][i]);
			try {
				this.account_labels.push(
					this.bankStatement['accounts'][i]['type'].concat(
						' (' + this.bankStatement['accounts'][i]['account'] + ')'
					)
				);
			} catch (err) {
				this.account_labels.push(this.bankStatement['accounts'][i]['type']);
			}
		}

		if (selectAfterAdding) {
			this.selected.setValue(this.accounts[0]);
		}
	}

	clearBankStatement() {
		this.bankStatement = null;
		this.account_labels = [];
		this.accounts = [];
	}

	async showInveriteBankStatements(guid) {
		this.apiService.showInveriteBankStatements(guid).subscribe(async (res) => {
			if (res.hasOwnProperty('error')) {
				alert(res['error']);
			} else {
				this.bankStatement = res;
				this.addTab(false);
			}
		});
	}

	getApplicant(applicant: String): String {
		switch (applicant) {
			case 'MAIN_APPLICANT':
				return 'Main applicant';
			case 'CO_APPLICANT':
				return 'Co applicant';
			default:
				return 'Main applicant';
		}
	}

	getBankNameFromId(bankId: number): string {
		return this.inveriteBanks.find((bank) => bank.bankID === bankId).name;
	}

	viewBankStatementOnFlinks(flinksRequest: FlinksRequest) {
		let applicantName: string;
		if (flinksRequest.applicant === 'MAIN_APPLICANT') {
			applicantName = this.application.Deal_Name;
		} else if (flinksRequest.applicant === 'CO_APPLICANT') {
			applicantName = this.application.Co_Applicant_Name;
		}
		if (applicantName) {
			window.open(
				`https://dashboard.flinks.com/en/413bd5c6-ee44-47ef-8f4d-125b6ce7bfd8/insights/requests?terms=${encodeURIComponent(
					applicantName
				)}`,
				'_blank'
			);
		}
	}

	onBankingMethodChange(bankingMethod: string) {
		if (this.application) {
			this.applicationService
				.updateApplication(this.application.id, {
					Banking_method: bankingMethod,
					First_Name: this.application.Contact_Name.First_Name,
					Middle_Name: this.application.Contact_Name.Middle_Name,
					Last_Name: this.application.Contact_Name.Last_Name,
				})
				.subscribe({
					next: () => {
						this.application.Banking_method = bankingMethod;
					},
					error: (res) => {
						console.error(res.error.message);
						this._snackBar.open(
							res.error.message,
							SnackbarConstants.ACTION_DISMISS,
							SnackbarConstants.DURATION_DISMISS_SHORT
						);
					},
				});
		}
	}

	ngOnDestroy() {
		this._onDestroy.next();
		this._onDestroy.complete();
	}
}
